"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  List,
  Tabs,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
  IconPhoto,
  IconDroplet,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";
import OutfitGrid from "@/components/OutfitGrid";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

export default function SoftSummerVsCoolSummerContent() {
  const t = useTranslations("softSummerVsCoolSummer");

  // Get the subseason data
  const softSummerData = colorSeasons.SUMMER.subseasons.TRUE; // TRUE is Soft Summer in the constants
  const coolSummerData = colorSeasons.SUMMER.subseasons.COOL;

  // State for outfit data
  const [softSummerOutfits, setSoftSummerOutfits] = useState<OutfitIdea[]>([]);
  const [coolSummerOutfits, setCoolSummerOutfits] = useState<OutfitIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const [softSummerDoc, coolSummerDoc] = await Promise.all([
          getDoc(doc(db, "outfitIdeas", "SUMMER_TRUE")),
          getDoc(doc(db, "outfitIdeas", "SUMMER_COOL")),
        ]);

        setSoftSummerOutfits(softSummerDoc.data()?.ideas || []);
        setCoolSummerOutfits(coolSummerDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutfitData();
  }, []);

  const comparisonPoints = [
    {
      aspect: "Color Intensity",
      softSummer: "Muted and soft colors with gentle saturation",
      coolSummer: "Cool colors with more depth and clarity",
      icon: IconPalette,
    },
    {
      aspect: "Undertone",
      softSummer: "Cool undertones with subtle warmth",
      coolSummer: "Purely cool undertones throughout",
      icon: IconEye,
    },
    {
      aspect: "Best For",
      softSummer: "Delicate, ethereal coloring",
      coolSummer: "Clear, crisp coloring with contrast",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      softSummer: "Bright, saturated colors",
      coolSummer: "Warm, golden tones",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Soft Summer vs Cool Summer
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Discover the key differences between these two cool summer subseasons and find which one perfectly
            complements your natural coloring.
          </Text>

          {/* Season Moodboard */}
          <Box mb="xl">
            <Box
              mih={{ base: "300px", md: "400px" }}
              style={{
                position: "relative",
                borderRadius: "1rem",
                overflow: "hidden",
              }}
            >
              <Image
                src="/SUMMER.webp"
                alt="Summer season moodboard"
                width={800}
                height={400}
                style={{
                  borderRadius: "1rem",
                  objectFit: "cover",
                  objectPosition: "center",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison Overview
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{softSummerData.emoji}</Text>
                <Title order={3}>Soft Summer</Title>
                <Text size="sm" c="dimmed">
                  {softSummerData.description.substring(0, 150)}...
                </Text>
                <Badge color="blue" variant="light">
                  Muted & Gentle
                </Badge>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{coolSummerData.emoji}</Text>
                <Title order={3}>Cool Summer</Title>
                <Text size="sm" c="dimmed">
                  {coolSummerData.description.substring(0, 150)}...
                </Text>
                <Badge color="blue" variant="light">
                  Cool & Clear
                </Badge>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="blue" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Soft Summer:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.softSummer}
                    </Text>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Cool Summer:
                    </Text>
                    <Text size="sm">{point.coolSummer}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Detailed Color Palette Comparison */}
        <Tabs defaultValue="soft-summer" variant="outline">
          <Tabs.List justify="center" mb="xl">
            <Tabs.Tab value="soft-summer" leftSection={<IconDroplet size={16} />}>
              Soft Summer Palette
            </Tabs.Tab>
            <Tabs.Tab value="cool-summer" leftSection={<IconDroplet size={16} />}>
              Cool Summer Palette
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="soft-summer">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                Soft Summer Color Palette
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                Muted, soft colors with gentle saturation that complement cool undertones
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Primary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {softSummerData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Neutral Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {softSummerData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Complementary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {softSummerData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="cool-summer">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                Cool Summer Color Palette
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                Cool-toned colors with medium saturation that enhance cool undertones
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Primary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {coolSummerData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Neutral Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {coolSummerData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Complementary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {coolSummerData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Characteristics Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Characteristics & Styling
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} mb="md" ta="center" c="blue">
                Soft Summer
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {softSummerData.characteristics.map((char, index) => (
                      <Badge key={index} color="blue" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {softSummerData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center" c="blue">
                Cool Summer
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {coolSummerData.characteristics.map((char, index) => (
                      <Badge key={index} color="blue" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {coolSummerData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Outfit Samples */}
        <Divider my="xl" />
        <Box>
          <Title order={2} mb="lg" ta="center">
            Outfit Inspiration
          </Title>
          <Text size="sm" c="dimmed" ta="center" mb="xl">
            See how these color palettes translate into real outfit combinations
          </Text>

          <Tabs defaultValue="soft-summer-outfits" variant="outline">
            <Tabs.List justify="center" mb="xl">
              <Tabs.Tab value="soft-summer-outfits" leftSection={<IconPhoto size={16} />}>
                Soft Summer Outfits
              </Tabs.Tab>
              <Tabs.Tab value="cool-summer-outfits" leftSection={<IconPhoto size={16} />}>
                Cool Summer Outfits
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="soft-summer-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>Loading outfit samples...</Text>
                </Box>
              ) : softSummerOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={softSummerOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">No outfit samples available for Soft Summer</Text>
                </Box>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="cool-summer-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>Loading outfit samples...</Text>
                </Box>
              ) : coolSummerOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={coolSummerOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">No outfit samples available for Cool Summer</Text>
                </Box>
              )}
            </Tabs.Panel>
          </Tabs>
        </Box>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Fabric Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Hold a muted dusty rose fabric and a clear cool pink near your face in natural light:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Soft Summer:</strong> The dusty rose makes your skin look healthier</List.Item>
                <List.Item><strong>Cool Summer:</strong> The clear cool pink brightens your face</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Jewelry Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare brushed silver vs polished silver jewelry:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Soft Summer:</strong> Brushed/matte silver looks more harmonious</List.Item>
                <List.Item><strong>Cool Summer:</strong> Shiny polished silver enhances your features</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between Soft Summer and Cool Summer.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/summer/true"
                size="sm"
                color="gray"
              >
                View Soft Summer Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/summer/cool"
                size="sm"
                color="gray"
              >
                View Cool Summer Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
